import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { trackerKeys } from './hooks';
import type { SupabaseClient } from '../../lib/supabase';
import type { Tables } from '../../types/database.types';

/**
 * Filling Lead Source on a whole group at once, and taking it back.
 *
 * 569 of 688 leads have no Lead Source. Needs Review groups them by evidence
 * the Hub already holds; this is what happens when somebody confirms a group.
 *
 * ── The safety property is in the SQL, not here ────────────────────────────
 *
 * `bulk_fill_lead_source` carries `and source is null` inside the function. So
 * when Nez and Ced work the queue at the same time, the second call fills
 * fewer rows rather than overwriting the first — no lock, no claim table, and
 * no answer ever lost. The browser's copy of the list from thirty seconds ago
 * does not get a vote.
 *
 * Proved twice over in the schema harness: removing that clause fails the
 * assertion, and it also trips `leads_consent_guard`, which refuses any change
 * to a Lead Source that is already set. Two independent refusals for one
 * mistake.
 *
 * ── Why filling is open and reverting is not ───────────────────────────────
 *
 * Filling a blank is open to the sales team: it answers a question nobody had
 * answered. Reverting changes leads that now carry a source, which is the
 * admin-only side of that line — so it needs an admin and a stated reason,
 * exactly like correcting one lead by hand.
 *
 * Kyu asked for the revert so a wrong batch is one click rather than 212
 * corrections. It is safe to offer precisely because it un-answers: back to
 * blank is never a claim about anybody.
 */

export type LeadSourceBatch = Tables<'lead_source_batches'>;

const BATCH_COLUMNS =
  'id, applied_source, rule, lead_ids, actor_id, created_at, reverted_at, reverted_by, revert_reason';

const batchKeys = {
  all: [...trackerKeys.all, 'source-batches'] as const,
};

export async function fetchRecentBatches(client: SupabaseClient): Promise<LeadSourceBatch[]> {
  const { data, error } = await client
    .from('lead_source_batches')
    .select(BATCH_COLUMNS)
    .is('reverted_at', null)
    .order('created_at', { ascending: false })
    // Five is a working memory, not a history. Somebody who has just filled a
    // group wants the last thing they did within reach; anything older is an
    // audit question, and sales_activity is where audit questions are answered.
    .limit(5);
  if (error) {
    throw new Error(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export function useRecentLeadSourceBatches(enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: batchKeys.all,
    queryFn: () => fetchRecentBatches(client),
    enabled,
    retry: false,
  });
}

export function useBulkFillLeadSource() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      leadIds: string[];
      source: 'inbound' | 'outbound';
      rule: string;
    }): Promise<string> => {
      const { data, error } = await client.rpc('bulk_fill_lead_source', {
        p_leads: input.leadIds,
        p_source: input.source,
        p_rule: input.rule,
      });
      if (error) {
        throw new Error(error.message);
      }
      return typeof data === 'string' ? data : '';
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
      void queryClient.invalidateQueries({ queryKey: batchKeys.all });
    },
  });
}

export function useRevertLeadSourceBatch() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { batchId: string; reason: string }): Promise<number> => {
      const { data, error } = await client.rpc('revert_lead_source_batch', {
        p_batch: input.batchId,
        p_reason: input.reason,
      });
      if (error) {
        throw new Error(error.message);
      }
      return typeof data === 'number' ? data : 0;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
      void queryClient.invalidateQueries({ queryKey: batchKeys.all });
    },
  });
}

/** The database's refusals, in words somebody can act on. */
export function batchErrorMessage(raw: string): string {
  if (raw.includes('nothing_left_to_fill')) {
    // The honest reading, and the common one: somebody else cleared this group
    // while you were looking at it. Nothing went wrong and nothing was lost.
    return 'These leads already have a Lead Source — somebody got here first. Refresh to see the current list.';
  }
  if (raw.includes('fill_rule_required')) {
    return 'A bulk fill has to record why it was applied.';
  }
  if (raw.includes('no_leads_given')) {
    return 'Select some leads first.';
  }
  if (raw.includes('batch_revert_needs_admin')) {
    return 'Only a TAP admin can undo a bulk fill.';
  }
  if (raw.includes('batch_revert_reason_required')) {
    return 'Give a reason for undoing this batch. It is written to the log with your name.';
  }
  if (raw.includes('batch_already_reverted')) {
    return 'This batch has already been undone.';
  }
  if (raw.includes('lead_consent_is_permanent')) {
    return 'Some of these leads already have a Lead Source, which cannot be changed here.';
  }
  if (raw.includes('not_a_sales_member')) {
    return 'The Sales Tracker is limited to the sales team.';
  }
  return 'That could not be saved. Try again.';
}

/** "212 leads set to Outbound — On an outbound list", for the undo strip. */
export function batchSummary(batch: LeadSourceBatch): string {
  const count = batch.lead_ids.length;
  const side = batch.applied_source === 'inbound' ? 'Inbound' : 'Outbound';
  return `${String(count)} ${count === 1 ? 'lead' : 'leads'} set to ${side} — ${batch.rule}`;
}
